import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAdminHeaders,
  hasPermission,
  PERMS,
  validateAdminSession,
} from '../../utils/adminAuth';
import './ActivityLogPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const PAGE_SIZE = 30;

interface ActivityEntry {
  id: number;
  createdAt: string;
  actorUsername: string;
  actorRole: string;
  actorAdminUserId?: number;
  action: string;
  entityType?: string;
  entityId?: string;
  summary: string;
  subjectName?: string;
  details?: Record<string, unknown>;
}

type SearchTarget = 'admin' | 'volunteer';

interface ActorSuggestion {
  username: string;
  role: string;
}

interface NameSuggestion {
  kind: 'actor' | 'volunteer';
  label: string;
  username?: string;
  role?: string;
  name?: string;
}

interface ActivityFilter {
  actor: string;
  actorUsername: string;
  actorRole: string;
  subject: string;
}

const roleLabel = (role: string): string => {
  switch (role) {
    case 'supervisor':
      return 'Supervisor';
    case 'breakglass':
      return 'Break-glass';
    case 'admin':
      return 'Admin';
    default:
      return role || 'Unknown';
  }
};

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const FIELD_LABELS: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone_number: 'Phone',
  zip_code: 'Zip code',
  birth_month: 'Birth month',
  emergency_first_name: 'Emergency contact first name',
  emergency_last_name: 'Emergency contact last name',
  emergency_phone_number: 'Emergency contact phone',
  emergency_relationship: 'Emergency contact relationship',
  is_active: 'Active',
  auto_checkout: 'Auto-checkout',
  passcode: 'User code',
  username: 'Username',
  role: 'Role',
  admin_override: 'Admin override',
  mode: 'Print mode',
  total_rows: 'Rows in file',
  imported: 'Imported',
  skipped: 'Skipped',
  skipped_emails: 'Skipped emails',
  error_count: 'Errors',
  permissions: 'Permissions',
  volunteer_user_id: 'Volunteer ID',
};

const formatFieldLabel = (key: string): string =>
  FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatDetailValue = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const ActivityLogPage: React.FC = () => {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [searchTarget, setSearchTarget] = useState<SearchTarget>('admin');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedActor, setSelectedActor] = useState<ActorSuggestion | null>(null);
  const [selectedVolunteer, setSelectedVolunteer] = useState('');
  const [suggestions, setSuggestions] = useState<NameSuggestion[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);
  const oldestIdRef = useRef<number | null>(null);
  const filterRef = useRef<ActivityFilter>({ actor: '', actorUsername: '', actorRole: '', subject: '' });
  const requestRef = useRef(0);

  useEffect(() => {
    const boot = async () => {
      const session = await validateAdminSession();
      if (!session) {
        navigate('/admin-login');
        return;
      }
      if (session.mustResetPassword) {
        navigate('/admin/change-password');
        return;
      }
      if (!hasPermission(PERMS.activityLogView, session) && !hasPermission(PERMS.activityLogRead, session)) {
        navigate('/admin-panel');
        return;
      }
      setIsValidating(false);
    };
    boot();
  }, [navigate]);

  const fetchPage = useCallback(async (replace: boolean) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      const filter = filterRef.current;
      if (filter.actorUsername) {
        params.set('actor_username', filter.actorUsername);
        if (filter.actorRole) params.set('actor_role', filter.actorRole);
      } else if (filter.actor) {
        params.set('actor', filter.actor);
      }
      if (filter.subject) params.set('subject', filter.subject);
      if (!replace && oldestIdRef.current) {
        params.set('before_id', String(oldestIdRef.current));
      }

      const response = await fetch(`${API_BASE_URL}/admin/activity-log?${params}`, {
        headers: getAdminHeaders(),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || 'Failed to load activity log');
        return;
      }

      const page: ActivityEntry[] = data.entries || [];
      setEntries((prev) => (replace ? page : [...prev, ...page]));
      setHasMore(Boolean(data.has_more));
      if (page.length > 0) {
        oldestIdRef.current = page[page.length - 1].id;
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  const pickingVolunteer = searchTarget === 'volunteer' && searchQuery.trim() !== '' && !selectedVolunteer;

  useEffect(() => {
    if (isValidating || pickingVolunteer) return;
    const handle = window.setTimeout(() => {
      filterRef.current = {
        actor: searchTarget === 'admin' && !selectedActor ? searchQuery.trim() : '',
        actorUsername: searchTarget === 'admin' && selectedActor ? selectedActor.username : '',
        actorRole: searchTarget === 'admin' && selectedActor ? selectedActor.role : '',
        subject: searchTarget === 'volunteer' ? selectedVolunteer : '',
      };
      oldestIdRef.current = null;
      if (listRef.current) listRef.current.scrollTop = 0;
      fetchPage(true);
    }, searchTarget === 'admin' && !selectedActor ? 250 : 0);
    return () => window.clearTimeout(handle);
  }, [isValidating, searchTarget, searchQuery, selectedActor, selectedVolunteer, pickingVolunteer, fetchPage]);

  useEffect(() => {
    if (searchQuery.trim().length < 1) {
      setSuggestions([]);
      setSuggesting(false);
      return;
    }
    if (searchTarget === 'volunteer' && selectedVolunteer && selectedVolunteer.toLowerCase() === searchQuery.trim().toLowerCase()) {
      setSuggestions([]);
      setSuggesting(false);
      return;
    }
    if (searchTarget === 'admin' && selectedActor) {
      setSuggestions([]);
      setSuggesting(false);
      return;
    }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setSuggesting(true);
    const handle = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          target: searchTarget === 'admin' ? 'actor' : 'subject',
          q: searchQuery.trim(),
        });
        const response = await fetch(`${API_BASE_URL}/admin/activity-log/names?${params}`, {
          headers: getAdminHeaders(),
        });
        const data = await response.json();
        if (requestRef.current !== requestId) return;
        if (!response.ok || !data.success) {
          setSuggestions([]);
          return;
        }
        const names = Array.isArray(data.names) ? data.names : [];
        if (searchTarget === 'admin') {
          setSuggestions(names.map((item: ActorSuggestion) => ({
            kind: 'actor' as const,
            username: item.username,
            role: item.role,
            label: `${item.username} · ${roleLabel(item.role)}`,
          })));
        } else {
          setSuggestions(names.map((item: { name: string }) => ({
            kind: 'volunteer' as const,
            name: item.name,
            label: item.name,
          })));
        }
      } catch {
        if (requestRef.current === requestId) setSuggestions([]);
      } finally {
        if (requestRef.current === requestId) setSuggesting(false);
      }
    }, 200);
    return () => window.clearTimeout(handle);
  }, [searchTarget, searchQuery, selectedActor, selectedVolunteer]);

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedActor(null);
    setSelectedVolunteer('');
    setSuggestions([]);
  };

  const chooseTarget = (next: SearchTarget) => {
    setSearchTarget(next);
    clearSearch();
  };

  const chooseSuggestion = (suggestion: NameSuggestion) => {
    if (suggestion.kind === 'actor' && suggestion.username && suggestion.role) {
      setSelectedActor({ username: suggestion.username, role: suggestion.role });
      setSearchQuery(suggestion.label);
    } else if (suggestion.name) {
      setSelectedVolunteer(suggestion.name);
      setSearchQuery(suggestion.name);
    }
    setSuggestions([]);
  };

  // Fill viewport if first page is short
  useEffect(() => {
    if (isValidating || loading || !hasMore || error || pickingVolunteer) return;
    const el = listRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 24) {
      fetchPage(false);
    }
  }, [entries, hasMore, loading, error, isValidating, pickingVolunteer, fetchPage]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el || !hasMore || loadingRef.current) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      fetchPage(false);
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBack = () => {
    navigate('/admin-panel');
  };

  const renderDetails = (details?: Record<string, unknown>) => {
    if (!details || Object.keys(details).length === 0) {
      return <p className="activity-details-empty">No field details</p>;
    }

    return (
      <ul className="activity-details-list">
        {Object.entries(details).map(([key, value]) => {
          if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            ('from' in (value as object) || 'to' in (value as object))
          ) {
            const diff = value as { from?: unknown; to?: unknown };
            return (
              <li key={key}>
                <strong>{formatFieldLabel(key)}</strong>:{' '}
                <span className="diff-from">{formatDetailValue(diff.from)}</span>
                <span className="diff-arrow"> → </span>
                <span className="diff-to">{formatDetailValue(diff.to)}</span>
              </li>
            );
          }
          return (
            <li key={key}>
              <strong>{formatFieldLabel(key)}</strong>:{' '}
              {formatDetailValue(value)}
            </li>
          );
        })}
      </ul>
    );
  };

  if (isValidating) {
    return (
      <div className="activity-log-container">
        <div className="activity-log-card">
          <div className="loading-spinner" />
          <p>Validating session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-log-container">
      <div className="activity-log-card">
        <div className="activity-log-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="activity-log-title">Activity Log</h1>
          <p className="activity-log-subtitle">
            {selectedVolunteer
              ? `Activity for ${selectedVolunteer}, newest first`
              : selectedActor
                ? `Changes by ${selectedActor.username}, newest first`
                : 'Append-only record of admin and supervisor changes, newest first'}
          </p>
        </div>

        <div className="activity-search">
          <div className="activity-search-panel">
            <div className="activity-search-targets" role="radiogroup" aria-label="Who to search">
              <label>
                <input
                  type="radio"
                  name="activity-search-target"
                  checked={searchTarget === 'admin'}
                  onChange={() => chooseTarget('admin')}
                />
                Admin
              </label>
              <label>
                <input
                  type="radio"
                  name="activity-search-target"
                  checked={searchTarget === 'volunteer'}
                  onChange={() => chooseTarget('volunteer')}
                />
                Volunteer
              </label>
            </div>
            <p className="activity-search-hint">
              {searchTarget === 'admin'
                ? 'Who made the change. Try Break-glass, or an admin username.'
                : 'Who the change was about. Pick a name to see that person’s activity.'}
            </p>
            <div className="activity-search-field">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedActor(null);
                  setSelectedVolunteer('');
                }}
                placeholder={searchTarget === 'admin' ? 'Search admins' : 'Search volunteer names'}
                aria-label={searchTarget === 'admin' ? 'Search admins' : 'Search volunteer names'}
              />
              {suggestions.length > 0 && (
                <ul className="activity-suggestions">
                  {suggestions.map((suggestion) => (
                    <li key={suggestion.label}>
                      <button type="button" onClick={() => chooseSuggestion(suggestion)}>
                        {suggestion.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div
          className="activity-log-list"
          ref={listRef}
          onScroll={handleScroll}
        >
          {error && <div className="activity-error">{error}</div>}
          {pickingVolunteer && !error && (
            <div className="activity-empty">
              {suggestions.length > 0 ? 'Choose a name.' : suggesting ? 'Looking up names…' : 'No matching names.'}
            </div>
          )}
          {!pickingVolunteer && !error && entries.length === 0 && !loading && (
            <div className="activity-empty">
              {searchQuery.trim() || selectedVolunteer || selectedActor
                ? 'No activity matches that search.'
                : 'No activity recorded yet.'}
            </div>
          )}
          {!pickingVolunteer && entries.map((entry) => {
            const expanded = expandedIds.has(entry.id);
            const hasDetails = entry.details && Object.keys(entry.details).length > 0;
            return (
              <div key={entry.id} className="activity-entry">
                <button
                  type="button"
                  className={`activity-entry-header ${expanded ? 'expanded' : ''}`}
                  onClick={() => toggleExpanded(entry.id)}
                  disabled={!hasDetails}
                >
                  <div className="activity-entry-meta">
                    <span className="activity-when">{formatWhen(entry.createdAt)}</span>
                    <span className="activity-actor">
                      {entry.actorUsername}
                      <span className={`role-chip role-${entry.actorRole}`}>
                        {roleLabel(entry.actorRole)}
                      </span>
                    </span>
                  </div>
                  <p className="activity-summary">{entry.summary}</p>
                  {hasDetails && (
                    <span className="activity-expand-hint">{expanded ? 'Hide details' : 'Show details'}</span>
                  )}
                </button>
                {expanded && hasDetails && (
                  <div className="activity-entry-body">{renderDetails(entry.details)}</div>
                )}
              </div>
            );
          })}
          {!pickingVolunteer && loading && <div className="activity-loading">Loading…</div>}
          {!pickingVolunteer && !loading && hasMore && (
            <button type="button" className="load-more-btn" onClick={() => fetchPage(false)}>
              Load older
            </button>
          )}
          {!pickingVolunteer && !loading && !hasMore && entries.length > 0 && (
            <div className="activity-end">End of log</div>
          )}
        </div>

        <div className="activity-log-footer">
          <button type="button" onClick={handleBack} className="back-button">
            ← Back to Admin Panel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPage;
